import { useState, useEffect } from "react";
import { Inbox, Trash2, Eye, Mail, Phone, Calendar, Search, ArrowLeft, MessageSquare } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/stats`),
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/read`);
      fetchData();
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`);
      toast.success("Contact deleted");
      setSelected(null);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const viewContact = (contact) => {
    setSelected(contact);
    if (!contact.is_read) markRead(contact.id);
  };

  const filtered = contacts.filter((c) =>
    [c.name, c.email, c.subject, c.message].some((field) =>
      (field || "").toLowerCase().includes(search.toLowerCase())
    )
  );

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F172A] text-white" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-['Barlow_Condensed'] font-bold text-2xl md:text-3xl">Admin Dashboard</h1>
              <p className="text-slate-400 text-sm mt-1">Contact Form Submissions</p>
            </div>
            <Link
              to="/"
              className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
              data-testid="admin-back-to-site"
            >
              <ArrowLeft size={16} /> Back to Site
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg p-5 border border-slate-200" data-testid="admin-stat-total">
            <div className="flex items-center gap-3">
              <div className="bg-[#EFF6FF] p-2.5 rounded-lg">
                <Inbox size={20} className="text-[#0EA5E9]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0F172A]">{stats.total}</div>
                <div className="text-xs text-slate-500">Total Submissions</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 border border-slate-200" data-testid="admin-stat-unread">
            <div className="flex items-center gap-3">
              <div className="bg-[#FFF7ED] p-2.5 rounded-lg">
                <MessageSquare size={20} className="text-[#F97316]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0F172A]">{stats.unread}</div>
                <div className="text-xs text-slate-500">Unread Messages</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 border border-slate-200 hidden md:block" data-testid="admin-stat-read">
            <div className="flex items-center gap-3">
              <div className="bg-[#F0FDF4] p-2.5 rounded-lg">
                <Eye size={20} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0F172A]">{stats.total - stats.unread}</div>
                <div className="text-xs text-slate-500">Read Messages</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border border-slate-200 mb-6">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3">
            <Search size={18} className="text-slate-400" />
            <Input
              placeholder="Search by name, email, subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 shadow-none focus-visible:ring-0 h-auto p-0"
              data-testid="admin-search-input"
            />
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading submissions...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="admin-no-results">
              <Inbox size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="font-medium">No submissions found</p>
              <p className="text-sm mt-1">Contact form submissions will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-contacts-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-[#0F172A]">Status</TableHead>
                    <TableHead className="font-semibold text-[#0F172A]">Name</TableHead>
                    <TableHead className="font-semibold text-[#0F172A]">Email</TableHead>
                    <TableHead className="font-semibold text-[#0F172A] hidden md:table-cell">Subject</TableHead>
                    <TableHead className="font-semibold text-[#0F172A] hidden lg:table-cell">Date</TableHead>
                    <TableHead className="font-semibold text-[#0F172A] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((contact) => (
                    <TableRow
                      key={contact.id}
                      className={`cursor-pointer hover:bg-slate-50 ${!contact.is_read ? "bg-blue-50/50" : ""}`}
                      onClick={() => viewContact(contact)}
                      data-testid={`admin-contact-row-${contact.id}`}
                    >
                      <TableCell>
                        <Badge variant={contact.is_read ? "secondary" : "default"} className={contact.is_read ? "bg-slate-100 text-slate-600" : "bg-[#0EA5E9] text-white"}>
                          {contact.is_read ? "Read" : "New"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-[#0F172A]">{contact.name}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{contact.email}</TableCell>
                      <TableCell className="text-slate-500 text-sm hidden md:table-cell">{contact.subject || "-"}</TableCell>
                      <TableCell className="text-slate-500 text-sm hidden lg:table-cell">{formatDate(contact.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteContact(contact.id); }}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`admin-delete-btn-${contact.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-contact-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">Message Details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3">
                <div className="bg-[#F1F5F9] p-2.5 rounded-full">
                  <Mail size={18} className="text-[#0EA5E9]" />
                </div>
                <div>
                  <div className="font-semibold text-[#0F172A]">{selected.name}</div>
                  <div className="text-sm text-slate-500">{selected.email}</div>
                </div>
              </div>
              {selected.phone && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone size={14} className="text-slate-400" /> {selected.phone}
                </div>
              )}
              {selected.subject && (
                <div className="text-sm">
                  <span className="font-medium text-[#0F172A]">Subject: </span>
                  <span className="text-slate-600">{selected.subject}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Calendar size={12} /> {formatDate(selected.created_at)}
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-slate-200">
                <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
              <div className="flex gap-3 pt-2">
                <a
                  href={`mailto:${selected.email}`}
                  className="btn-primary text-sm !py-2 !px-4 inline-flex items-center gap-2"
                  data-testid="admin-reply-btn"
                >
                  <Mail size={14} /> Reply via Email
                </a>
                <button
                  onClick={() => deleteContact(selected.id)}
                  className="text-sm text-red-500 hover:text-red-700 px-4 py-2 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  data-testid="admin-detail-delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
